<template>
  <div class="meal-holiday-config" :class="{ embedded: embedded }">
    <div v-if="!embedded" class="page-banner">
      <h2>排餐规则</h2>
      <p>超时扣减、每周休息日与法定节假日 / 调休补班统一配置。</p>
    </div>

    <!-- 超时扣减 -->
    <el-card class="rule-card" shadow="never">
      <div class="card-head">
        <div>
          <div class="card-title">超时扣减阈值</div>
          <div class="card-desc">超过截止时间未打印小票的餐次将自动扣减并顺延</div>
        </div>
        <el-button type="primary" class="btn-save" :loading="savingCutoff" @click="saveDeductionSettings">
          保存
        </el-button>
      </div>
      <el-form label-width="120px" class="cutoff-form" label-position="right">
        <el-form-item label="中餐截止时间">
          <el-time-select
            v-model="deductionSettings.lunch_cutoff"
            clearable
            class="cutoff-input"
            :picker-options="{ start: '08:00', step: '00:15', end: '14:00' }"
            placeholder="选择时间"
          />
        </el-form-item>
        <el-form-item label="晚餐截止时间">
          <el-time-select
            v-model="deductionSettings.dinner_cutoff"
            clearable
            class="cutoff-input"
            :picker-options="{ start: '15:00', step: '00:15', end: '22:00' }"
            placeholder="选择时间"
          />
        </el-form-item>
        <el-form-item label="每单延期上限">
          <el-input-number
            v-model="deductionSettings.max_deferrals_per_order"
            :min="0"
            :max="99"
            :step="1"
            controls-position="right"
          />
          <span class="inline-hint">次（0 = 不限制）</span>
        </el-form-item>
      </el-form>
      <p class="hint">留空表示该餐次不启用超时扣减；延期上限对小程序与管理端同时生效。</p>
    </el-card>

    <!-- 每周休息日 -->
    <el-card class="rule-card" shadow="never">
      <div class="card-head">
        <div>
          <div class="card-title">每周固定休息日</div>
          <div class="card-desc">优先级低于调休补班；补班日即使落在休息日也会排餐</div>
        </div>
        <el-button type="primary" class="btn-save" size="small" :loading="savingWeekly" @click="saveWeeklyRest">
          保存
        </el-button>
      </div>
      <el-checkbox-group v-model="weeklyRestDays" class="week-checks">
        <el-checkbox :label="1">周一</el-checkbox>
        <el-checkbox :label="2">周二</el-checkbox>
        <el-checkbox :label="3">周三</el-checkbox>
        <el-checkbox :label="4">周四</el-checkbox>
        <el-checkbox :label="5">周五</el-checkbox>
        <el-checkbox :label="6">周六</el-checkbox>
        <el-checkbox :label="0">周日</el-checkbox>
      </el-checkbox-group>
    </el-card>

    <!-- 节假日配置 -->
    <el-card class="rule-card" shadow="never">
      <div class="card-head">
        <div>
          <div class="card-title">节假日 / 调休配置</div>
          <div class="card-desc">内置 2025、2026 法定节假日，可一键导入后手动微调</div>
        </div>
        <div class="head-actions">
          <el-radio-group v-model="viewMode" size="small">
            <el-radio-button label="calendar">日历视图</el-radio-button>
            <el-radio-button label="list">列表视图</el-radio-button>
          </el-radio-group>
          <el-button
            type="success"
            size="small"
            icon="el-icon-download"
            :loading="importing"
            @click="importLegal"
          >
            一键导入当年法定节假日
          </el-button>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="showAddDialog">新增</el-button>
        </div>
      </div>

      <!-- 日历视图 -->
      <div v-if="viewMode === 'calendar'" v-loading="loadingList" class="cfg-calendar">
        <div class="cfg-cal-nav">
          <el-button circle size="mini" icon="el-icon-arrow-left" @click="prevCfgMonth" />
          <span>{{ cfgYear }}年{{ cfgMonth }}月</span>
          <el-button circle size="mini" icon="el-icon-arrow-right" @click="nextCfgMonth" />
          <el-select v-model="importYear" size="mini" style="width: 100px; margin-left: 12px;">
            <el-option :value="2025" label="2025" />
            <el-option :value="2026" label="2026" />
            <el-option :value="2027" label="2027" />
          </el-select>
        </div>
        <div class="cfg-grid">
          <div v-for="w in weekLabels" :key="'h'+w" class="cfg-head">{{ w }}</div>
          <div
            v-for="(day, idx) in cfgCells"
            :key="'c'+idx"
            class="cfg-cell"
            :class="cfgCellClass(day)"
            @click="day && day.inMonth && quickSetDay(day)"
          >
            <template v-if="day">
              <span class="cfg-num">{{ day.day }}</span>
              <span v-if="day.badge" class="cfg-badge">{{ day.badge }}</span>
              <span v-if="day.name && day.kind !== 'weekly_rest'" class="cfg-name">{{ day.name }}</span>
            </template>
          </div>
        </div>
        <p class="hint">点击日期循环切换：营业 → 休息 → 补班 → 营业（按周休规则）</p>
      </div>

      <!-- 列表视图 -->
      <div v-else>
        <el-table :data="holidayList" stripe style="width: 100%;" v-loading="loadingList">
          <el-table-column prop="config_date" label="日期" width="140" />
          <el-table-column label="类型" width="140">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.config_type === 'makeup_workday' ? '' : 'danger'">
                {{ scope.row.config_type === 'makeup_workday' ? '调休补班' : '法定节假日' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="名称" min-width="160" />
          <el-table-column label="操作" width="100" align="right">
            <template slot-scope="scope">
              <el-button size="mini" type="text" class="danger-text" @click="deleteRow(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>

    <el-dialog title="新增节假日 / 调休" :visible.sync="dialogVisible" width="480px">
      <el-form label-width="100px" label-position="right">
        <el-form-item label="日期">
          <el-date-picker v-model="form.config_date" type="date" value-format="yyyy-MM-dd" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="form.config_type" style="width: 100%;">
            <el-option label="法定节假日 / 自定义休息（跳过排餐）" value="holiday" />
            <el-option label="调休补班（正常排餐）" value="makeup_workday" />
          </el-select>
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model="form.name" placeholder="如：国庆节、调休补班" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="可选" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAdd">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { formatDate } from '../utils/calendarUtils'
import { formatApiError } from '../utils/apiError'

export default {
  name: 'MealHolidayConfig',
  props: {
    embedded: { type: Boolean, default: false }
  },
  data() {
    const now = new Date()
    return {
      deductionSettings: { lunch_cutoff: null, dinner_cutoff: null, max_deferrals_per_order: 0 },
      weeklyRestDays: [0, 6],
      holidayList: [],
      dayMeta: {},
      overrides: {},
      dialogVisible: false,
      form: { config_date: '', config_type: 'holiday', name: '', remark: '' },
      viewMode: 'calendar',
      cfgYear: now.getFullYear(),
      cfgMonth: now.getMonth() + 1,
      importYear: now.getFullYear(),
      importing: false,
      savingCutoff: false,
      savingWeekly: false,
      loadingList: false,
      weekLabels: ['一', '二', '三', '四', '五', '六', '日']
    }
  },
  computed: {
    cfgCells() {
      const y = this.cfgYear
      const m = this.cfgMonth - 1
      const first = new Date(y, m, 1)
      let startOffset = first.getDay() - 1
      if (startOffset < 0) startOffset = 6
      const daysInMonth = new Date(y, m + 1, 0).getDate()
      const prevDays = new Date(y, m, 0).getDate()
      const cells = []
      for (let i = 0; i < startOffset; i++) {
        const dayNum = prevDays - startOffset + 1 + i
        cells.push(this.buildCfgDay(new Date(y, m - 1, dayNum), false))
      }
      for (let i = 1; i <= daysInMonth; i++) {
        cells.push(this.buildCfgDay(new Date(y, m, i), true))
      }
      while (cells.length % 7 !== 0) {
        const n = cells.length - startOffset - daysInMonth + 1
        cells.push(this.buildCfgDay(new Date(y, m + 1, n), false))
      }
      return cells
    }
  },
  mounted() {
    this.loadSettings()
    this.loadWeeklyRest()
    this.loadHolidays()
    this.loadCfgMonth()
  },
  methods: {
    buildCfgDay(date, inMonth) {
      const dateStr = formatDate(date)
      const meta = this.dayMeta[dateStr] || {}
      return {
        date,
        dateStr,
        day: date.getDate(),
        inMonth,
        kind: meta.kind || 'workday',
        badge: meta.label || '',
        name: meta.name || '',
        isRest: !!meta.is_rest
      }
    },
    cfgCellClass(day) {
      if (!day) return ''
      return {
        'out-month': !day.inMonth,
        legal: day.kind === 'legal_holiday' || day.kind === 'custom_rest',
        weekly: day.kind === 'weekly_rest',
        makeup: day.kind === 'makeup'
      }
    },
    async loadSettings() {
      try {
        const resp = await this.$api.get('/admin/meal-schedules/deduction-settings')
        if (resp && resp.code === 200 && resp.data) {
          this.deductionSettings = {
            lunch_cutoff: resp.data.lunch_cutoff || null,
            dinner_cutoff: resp.data.dinner_cutoff || null,
            max_deferrals_per_order: Number(resp.data.max_deferrals_per_order) || 0
          }
        }
      } catch (e) {
        /* ignore */
      }
    },
    async loadWeeklyRest() {
      try {
        const resp = await this.$api.get('/admin/business-calendar/weekly-rest')
        if (resp && resp.code === 200 && resp.data && Array.isArray(resp.data.weekly_rest_days)) {
          this.weeklyRestDays = resp.data.weekly_rest_days.slice()
        }
      } catch (e) {
        /* ignore */
      }
    },
    async loadHolidays() {
      this.loadingList = true
      try {
        const resp = await this.$api.get('/admin/meal-schedules/holidays', {
          params: { pageSize: 500, year: this.cfgYear }
        })
        if (resp && resp.code === 200 && resp.data) {
          this.holidayList = resp.data.list || []
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '加载节假日失败'))
      } finally {
        this.loadingList = false
      }
    },
    async loadCfgMonth() {
      try {
        const resp = await this.$api.get('/admin/meal-schedules/calendar-month', {
          params: { year: this.cfgYear, month: this.cfgMonth }
        })
        if (resp && resp.code === 200 && resp.data) {
          this.dayMeta = resp.data.days || {}
          this.overrides = resp.data.overrides || {}
        }
      } catch (e) {
        /* ignore */
      }
    },
    prevCfgMonth() {
      if (this.cfgMonth === 1) {
        this.cfgYear--
        this.cfgMonth = 12
      } else {
        this.cfgMonth--
      }
      this.loadCfgMonth()
      this.loadHolidays()
    },
    nextCfgMonth() {
      if (this.cfgMonth === 12) {
        this.cfgYear++
        this.cfgMonth = 1
      } else {
        this.cfgMonth++
      }
      this.loadCfgMonth()
      this.loadHolidays()
    },
    async quickSetDay(day) {
      // 循环：auto(营业) -> holiday -> makeup -> auto
      let next = 'holiday'
      if (day.kind === 'makeup') next = 'auto'
      else if (day.kind === 'legal_holiday' || day.kind === 'custom_rest') next = 'makeup_workday'
      else if (day.kind === 'weekly_rest') next = 'makeup_workday'
      else next = 'holiday'
      try {
        await this.$api.put('/admin/meal-schedules/holidays/day', {
          config_date: day.dateStr,
          day_type: next
        })
        await this.loadCfgMonth()
        await this.loadHolidays()
        const map = { auto: '营业/周休', holiday: '休息日', makeup_workday: '补班日' }
        this.$message.success(`已设为「${map[next]}」`)
      } catch (e) {
        this.$message.error(formatApiError(e, '更新失败'))
      }
    },
    async importLegal() {
      this.importing = true
      try {
        const year = this.importYear || this.cfgYear
        const resp = await this.$api.post('/admin/meal-schedules/holidays/import-legal', {
          year,
          overwrite: true
        })
        if (resp && resp.code === 200) {
          this.$message.success(resp.message || '导入成功')
          this.cfgYear = year
          await this.loadHolidays()
          await this.loadCfgMonth()
        } else {
          this.$message.error((resp && resp.message) || '导入失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '导入失败'))
      } finally {
        this.importing = false
      }
    },
    async saveDeductionSettings() {
      this.savingCutoff = true
      try {
        const resp = await this.$api.put('/admin/meal-schedules/deduction-settings', this.deductionSettings)
        if (resp && resp.code === 200) this.$message.success('已保存')
        else this.$message.error((resp && resp.message) || '保存失败')
      } catch (e) {
        this.$message.error(formatApiError(e, '保存失败'))
      } finally {
        this.savingCutoff = false
      }
    },
    async saveWeeklyRest() {
      this.savingWeekly = true
      try {
        const resp = await this.$api.put('/admin/business-calendar/weekly-rest', {
          weekly_rest_days: this.weeklyRestDays
        })
        if (resp && resp.code === 200) {
          this.$message.success('每周休息日已保存')
          await this.loadCfgMonth()
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '保存失败'))
      } finally {
        this.savingWeekly = false
      }
    },
    showAddDialog() {
      this.form = { config_date: '', config_type: 'holiday', name: '', remark: '' }
      this.dialogVisible = true
    },
    async submitAdd() {
      if (!this.form.config_date) {
        this.$message.warning('请选择日期')
        return
      }
      try {
        const name = this.form.remark
          ? `${this.form.name || ''} ${this.form.remark}`.trim()
          : this.form.name
        const resp = await this.$api.post('/admin/meal-schedules/holidays', {
          config_date: this.form.config_date,
          config_type: this.form.config_type,
          name
        })
        if (resp && resp.code === 200) {
          this.$message.success('添加成功')
          this.dialogVisible = false
          await this.loadHolidays()
          await this.loadCfgMonth()
        } else {
          this.$message.error((resp && resp.message) || '添加失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '添加失败'))
      }
    },
    async deleteRow(row) {
      try {
        await this.$confirm(`确定删除 ${row.config_date} 的配置？`, '提示', { type: 'warning' })
        const resp = await this.$api.delete(`/admin/meal-schedules/holidays/${row.id}`)
        if (resp && resp.code === 200) {
          this.$message.success('已删除')
          await this.loadHolidays()
          await this.loadCfgMonth()
        }
      } catch (e) {
        if (e !== 'cancel') this.$message.error(formatApiError(e, '删除失败'))
      }
    }
  }
}
</script>

<style scoped>
.meal-holiday-config {
  padding: 4px 0 24px;
}
.page-banner {
  background: linear-gradient(135deg, #ecfdf5 0%, #f0f9ff 100%);
  border-radius: 14px;
  padding: 22px 26px;
  margin-bottom: 16px;
  border: 1px solid #d1fae5;
}
.page-banner h2 {
  margin: 0 0 6px;
  font-size: 20px;
  color: #0f172a;
}
.page-banner p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.rule-card {
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  margin-bottom: 16px;
}
.rule-card >>> .el-card__body {
  padding: 20px 22px;
}
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.card-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}
.card-desc {
  margin-top: 4px;
  font-size: 12px;
  color: #94a3b8;
}
.head-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}
.btn-save {
  border-radius: 10px;
  min-width: 88px;
}

.cutoff-form {
  max-width: 520px;
}
.cutoff-input {
  width: 220px;
}
.cutoff-input >>> .el-input__inner {
  border-radius: 10px;
  height: 40px;
}

.week-checks {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}
.inline-hint {
  margin-left: 10px;
  color: #94a3b8;
  font-size: 13px;
}
.hint {
  margin: 12px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

.cfg-cal-nav {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  font-weight: 700;
  color: #0f172a;
}
.cfg-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}
.cfg-head {
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  padding: 4px 0;
}
.cfg-cell {
  min-height: 64px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 6px;
  cursor: pointer;
  position: relative;
}
.cfg-cell.out-month { opacity: 0.35; cursor: default; }
.cfg-cell.legal { background: #fef2f2; border-color: #fecaca; }
.cfg-cell.weekly { background: #f8fafc; }
.cfg-cell.makeup { background: #eff6ff; border-color: #bfdbfe; }
.cfg-num { font-size: 13px; font-weight: 700; color: #0f172a; }
.cfg-cell.legal .cfg-num { color: #dc2626; }
.cfg-cell.makeup .cfg-num { color: #2563eb; }
.cfg-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 10px;
  font-weight: 700;
  padding: 0 5px;
  border-radius: 4px;
  background: #fecaca;
  color: #b91c1c;
}
.cfg-cell.makeup .cfg-badge {
  background: #bfdbfe;
  color: #1d4ed8;
}
.cfg-name {
  display: block;
  margin-top: 10px;
  font-size: 11px;
  color: #dc2626;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.danger-text { color: #ef4444 !important; }

@media (max-width: 768px) {
  .cutoff-input { width: 100%; }
  .cfg-grid { min-width: 560px; }
  .cfg-calendar { overflow-x: auto; }
}
</style>
